import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  makeStyles,
  Typography,
  Button,
  Checkbox,
  Radio,
  FormControl,
  FormControlLabel,
  RadioGroup,
  CircularProgress
} from '@material-ui/core'

const useStyles = makeStyles(() => ({
  root: {
    padding: 20,
    borderRadius: 6,
    border: '1px solid var(--color-warning-200, #fde68a)',
    background: 'var(--color-warning-050, #fffbeb)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  title: {
    fontSize: 14,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-warning-800, #92400e)'
  },
  reason: {
    fontSize: 13,
    color: 'var(--color-warning-900, #78350f)',
    lineHeight: 1.5
  },
  question: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '12px 14px',
    background: 'var(--opal-surface-default, #fff)',
    border: '1px solid var(--color-warning-300, #fcd34d)',
    borderRadius: 4
  },
  prompt: {
    fontSize: 13,
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-neutral-800, #1f2937)'
  },
  optionLabel: {
    fontSize: 13,
    color: 'var(--color-neutral-800, #1f2937)'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8
  }
}))

export default function QuestionsCard ({ questions, reason, isSubmitting, onSubmit }) {
  const classes = useStyles()
  const [selections, setSelections] = useState({})

  const allAnswered = useMemo(() => {
    return (questions || []).every(q => {
      const sel = selections[q.id]
      return Array.isArray(sel) && sel.length > 0
    })
  }, [questions, selections])

  if (!Array.isArray(questions) || questions.length === 0) return null

  const updateSingle = (qId, optionId) => {
    setSelections(prev => ({ ...prev, [qId]: [optionId] }))
  }
  const toggleMulti = (qId, optionId) => {
    setSelections(prev => {
      const cur = prev[qId] || []
      const next = cur.includes(optionId) ? cur.filter(id => id !== optionId) : [...cur, optionId]
      return { ...prev, [qId]: next }
    })
  }

  const submit = () => {
    const answers = questions.map(q => ({
      questionId: q.id,
      optionIds: selections[q.id] || []
    }))
    onSubmit?.(answers)
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography component='div' className={classes.title}>The agent needs your input</Typography>
        {reason && <Typography component='div' className={classes.reason}>{reason}</Typography>}
      </div>
      {questions.map(q => (
        <div key={q.id} className={classes.question}>
          <Typography component='div' className={classes.prompt}>{q.prompt}</Typography>
          {q.allowMultiple ? (
            <FormControl component='fieldset'>
              {q.options.map(opt => {
                const selected = (selections[q.id] || []).includes(opt.id)
                return (
                  <FormControlLabel
                    key={opt.id}
                    control={
                      <Checkbox
                        size='small'
                        checked={selected}
                        onChange={() => toggleMulti(q.id, opt.id)}
                      />
                    }
                    label={<span className={classes.optionLabel}>{opt.label}</span>}
                  />
                )
              })}
            </FormControl>
          ) : (
            <FormControl component='fieldset'>
              <RadioGroup
                value={(selections[q.id] || [])[0] || ''}
                onChange={(e) => updateSingle(q.id, e.target.value)}
              >
                {q.options.map(opt => (
                  <FormControlLabel
                    key={opt.id}
                    value={opt.id}
                    control={<Radio size='small' />}
                    label={<span className={classes.optionLabel}>{opt.label}</span>}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </div>
      ))}
      <div className={classes.actions}>
        <Button
          variant='contained'
          color='primary'
          size='small'
          disabled={!allAnswered || isSubmitting}
          onClick={submit}
          startIcon={isSubmitting ? <CircularProgress size={14} /> : null}
        >
          {isSubmitting ? 'Submitting…' : 'Submit answers'}
        </Button>
      </div>
    </div>
  )
}

QuestionsCard.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    prompt: PropTypes.string.isRequired,
    allowMultiple: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })).isRequired
  })),
  reason: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func
}
